import { APP_ENVIRONMENT } from "../../../_RuntimeConfig/_Config/Acomodar/AppEnvironment.js";
import { APP_STORAGE } from "../../../_RuntimeConfig/_Config/Acomodar/AppConfig.Storage.js";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const IS_PERSIST_TOKEN = APP_ENVIRONMENT.AUTH.PERSIST_SESSION;
const STORAGE_KEY = APP_STORAGE.APP_SESSION;

const AppAuthContext = createContext(null);

export const useAuth = () => {

    const CONTEXT = useContext(AppAuthContext);

    if (!CONTEXT) {
        throw new Error('useAuth() se usó fuera de <AppAuthProvider>. Revisá App.js');
    }

    return CONTEXT;
};

export const AppAuthProvider = ({ children, onLoginRequest = null, onRestoreRequest = null, onLogoutRequest = null }) => {

    const [useNameUserState, setNameUserState] = useState(null);
    const [useNameTokenState, setNameTokenState] = useState(null);
    const [useNameLoadingState, setNameLoadingState] = useState(true);

    useEffect(() => {

        let isActive = true;

        const onRestore = async () => {
            try {
                if (IS_PERSIST_TOKEN) {
                    const RAW = window.localStorage.getItem(STORAGE_KEY);
                    if (RAW) {
                        const SESSION = JSON.parse(RAW);
                        if (isActive) { setNameUserState(SESSION.user); setNameTokenState(SESSION.token); }
                    }
                }
                if (onRestoreRequest) {
                    const USER = await onRestoreRequest();
                    if (isActive && USER) setNameUserState(USER);
                }
            } catch {
            } finally {
                if (isActive) setNameLoadingState(false);
            }
        };
        onRestore();
        return () => { isActive = false; };

    }, [onRestoreRequest]);

    const cb__loginCallback = useCallback(async (credentials) => {

        const RESULT = await onLoginRequest?.(credentials);

        if (!RESULT?.user) throw new Error('No se pudo iniciar sesión');

        setNameUserState(RESULT.user);
        setNameTokenState(RESULT.token ?? null);

        if (IS_PERSIST_TOKEN) {
            try {
                window.localStorage.setItem(STORAGE_KEY, JSON.stringify(RESULT));
            } catch { }
        }

        return RESULT.user;

    }, [onLoginRequest]);

    const cb__logoutCallback = useCallback(async () => {

        try { await onLogoutRequest?.(); } catch { /* igual cerramos local */ }

        setNameUserState(null);
        setNameTokenState(null);

        try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* nada */ }

    }, [onLogoutRequest]);

    const cb__hasRoleCallback = useCallback((roles) => {

        if (!roles || roles.length === 0) return true;

        if (!useNameUserState) return false;

        const LIST = Array.isArray(roles) ? roles : [roles];

        return LIST.includes(useNameUserState.role);

    }, [useNameUserState]);

    const cb__hasPermissionCallback = useCallback((permissions) => {

        if (!permissions || permissions.length === 0) return true;

        if (!useNameUserState) return false;

        const LIST = Array.isArray(permissions) ? permissions : [permissions];

        const OWNED = useNameUserState.permissions ?? [];

        return LIST.some((p) => OWNED.includes(p));

    }, [useNameUserState]);

    const cb__hasEveryPermissionCallback = useCallback((permissions) => {

        if (!permissions || permissions.length === 0) return true;

        if (!useNameUserState) return false;

        const OWNED = useNameUserState.permissions ?? [];

        return permissions.every((p) => OWNED.includes(p));

    }, [useNameUserState]);

    const memo__authContextValueMemo = useMemo(() => ({
        user: useNameUserState, token: useNameTokenState, isAuth: !!useNameUserState,
        isLoading: useNameLoadingState, login: cb__loginCallback, logout: cb__logoutCallback,
        setUser: setNameUserState, hasRole: cb__hasRoleCallback, hasPermission: cb__hasPermissionCallback,
        hasEveryPermission: cb__hasEveryPermissionCallback,
    }), [
        useNameUserState, useNameTokenState, useNameLoadingState, cb__loginCallback, cb__logoutCallback, cb__hasRoleCallback,
        cb__hasPermissionCallback, cb__hasEveryPermissionCallback,
    ]);

    return <AppAuthContext.Provider value={memo__authContextValueMemo}>{children}</AppAuthContext.Provider>;
};
